import { FormState } from './Home';
import {
    FEDERAL_CERTIFICATIONS,
    requiresTradesAndLicenses,
    DEFAULT_COUNTRY,
} from './formConstants';

// Complete form validation result
export interface FormValidationResult {
    isValid: boolean;
    fieldErrors: Record<string, string>;
}

// Helper: Check if field has a real value (not empty)
function hasValue(value: unknown): boolean {
    if (value === undefined || value === null) return false;
    if (typeof value === 'number') return true; // Numbers are always "filled"
    if (typeof value === 'string') return value.trim().length > 0;
    if (typeof value === 'object') return true; // File objects, etc. are "filled"
    return false;
}

// Helper: Check numeric field
function hasPositiveNumber(value: number | undefined | null): boolean {
    return typeof value === 'number' && value > 0;
}

// Validate required basic fields
function validateBasicFields(form: FormState): Record<string, string> {
    const errors: Record<string, string> = {};

    if (!hasValue(form.vendorName)) {
        errors.vendorName = 'Please provide company name';
    }
    if (!hasValue(form.vendorType)) {
        errors.vendorType = 'Please select vendor type';
    }
    if (!hasValue(form.city)) {
        errors.city = 'Please provide a city';
    }
    if (!hasValue(form.state)) {
        errors.state = 'Please select a state/province';
    }
    if (!hasValue(form.billingAddress)) {
        errors.billingAddress = 'Please fill in an address';
    }

    // Zip is required only for USA
    if (form.country === DEFAULT_COUNTRY && !hasValue(form.zip)) {
        errors.zip = 'Please provide a zipcode';
    }

    return errors;
}

// Validate physical address (when different from billing)
function validatePhysicalAddress(form: FormState): Record<string, string> {
    const errors: Record<string, string> = {};

    if (form.sameAddress) {
        return errors; // Physical address same as billing, no validation needed
    }

    if (!hasValue(form.p_city)) {
        errors.p_city = 'Please provide a city';
    }
    if (!hasValue(form.p_state)) {
        errors.p_state = 'Please select a state/province';
    }
    if (!hasValue(form.p_Address)) {
        errors.p_Address = 'Please fill in an address';
    }
    if (form.p_country === DEFAULT_COUNTRY && !hasValue(form.p_zip)) {
        errors.p_zip = 'Please provide a zipcode';
    }

    return errors;
}

// Validate contact information
function validateContactInfo(form: FormState): Record<string, string> {
    const errors: Record<string, string> = {};

    if (!hasValue(form.contactName)) {
        errors.contactName = 'Please provide a contact name';
    }
    if (!hasValue(form.contactEmail)) {
        errors.contactEmail = 'Please provide a contact email';
    }
    if (!hasValue(form.businessPhone)) {
        errors.businessPhone = 'Please fill in a phone number';
    }

    return errors;
}

// Validate business details
function validateBusinessDetails(form: FormState): Record<string, string> {
    const errors: Record<string, string> = {};

    if (!hasPositiveNumber(form.yearsInBusiness)) {
        errors.yearsInBusiness = 'Please fill in valid year amount';
    }
    if (!hasPositiveNumber(form.numberOfEmployees)) {
        errors.numberOfEmployees = 'Please fill in valid number';
    }
    if (!hasValue(form.union)) {
        errors.union = 'Please select labor status';
    }
    if (!hasValue(form.notes)) {
        errors.notes = 'Please provide a description';
    }
    if (!hasValue(form.referral)) {
        errors.referral = 'Please select an option';
    }

    return errors;
}

// Validate W9 form
function validateW9(form: FormState): Record<string, string> {
    const errors: Record<string, string> = {};

    // W9 is valid if there's already one attached OR a new filename is set
    const hasExistingW9 = form.attachments.some((x) => x.type === 'W9');
    const hasNewW9 = hasValue(form.w9Form);

    if (!hasExistingW9 && !hasNewW9) {
        errors.w9Form = 'Please submit your W9 form';
    }

    return errors;
}

// Validate Evidence of Coverage / Proof of Insurance
function validateEvidenceOfCoverage(form: FormState): Record<string, string> {
    const errors: Record<string, string> = {};

    // EOC is valid if there's already one attached OR a new filename is set
    const hasExistingEOC = form.attachments.some((x) => x.type === 'EOC');
    const hasNewEOC = hasValue(form.evidenceOfCoverage);

    if (!hasExistingEOC && !hasNewEOC) {
        errors.evidenceOfCoverage = 'Please submit your Evidence of Coverage / Proof of Insurance';
    }

    return errors;
}

// Validate federal certification attachments
function validateFederalCertifications(form: FormState): Record<string, string> {
    const errors: Record<string, string> = {};

    for (const [key, cert] of Object.entries(FEDERAL_CERTIFICATIONS)) {
        if (form.federalCertification.includes(cert.name)) {
            // Check if already has attachment OR new file selected
            const hasExisting = form.attachments.some((x) => x.type === key);
            const formValue = (form as unknown as Record<string, unknown>)[cert.formField] as
                | string
                | undefined;
            const hasNew = hasValue(formValue);

            if (!hasExisting && !hasNew) {
                errors[cert.formField] = 'Please submit valid certification';
            }
        }
    }

    return errors;
}

// Validate trades and service areas (conditional on vendor type)
function validateTradesAndServiceAreas(
    form: FormState,
    selectedTradesCount: number,
    selectedServiceAreasCount: number
): Record<string, string> {
    const errors: Record<string, string> = {};

    if (!requiresTradesAndLicenses(form.vendorType)) {
        return errors; // Material suppliers don't need trades/service areas
    }

    if (selectedTradesCount === 0) {
        errors.trades = 'Please select at least one trade';
    }
    if (selectedServiceAreasCount === 0) {
        errors.serviceAreas = 'Please select at least one service area';
    }

    return errors;
}

// Validate held licenses
function validateHeldLicenses(form: FormState): Record<string, string> {
    const errors: Record<string, string> = {};

    if (!requiresTradesAndLicenses(form.vendorType)) {
        return errors; // Material suppliers don't need licenses
    }

    // Require at least one license for consultants, subcontractors, and sub+suppliers
    if (form.heldLicenses.length === 0) {
        errors.heldLicenses = 'Please provide at least one held license';
        return errors;
    }

    // Check that all license rows have required fields filled in
    const hasIncompleteLicense = form.heldLicenses.some((license) => {
        return !license.licenseType?.trim() || !license.licenseNumber?.trim();
    });

    if (hasIncompleteLicense) {
        errors.heldLicenses = 'Please complete all license fields (type and number are required)';
        return errors;
    }

    // Check that all licenses have files (unless they already have a file on the server)
    const hasInvalidLicenseFiles = form.heldLicenses.some((license, idx) => {
        // For existing licenses with a filePath on server, no new file is needed
        if (license?.filePath && license.filePath.trim().length > 0) return false;
        // Otherwise a new file must be selected
        return !form.licenseFiles[idx];
    });

    if (hasInvalidLicenseFiles) {
        errors.licenseFiles = 'Please upload files for all licenses';
    }

    return errors;
}

// Main validation function
export function validateForm(
    form: FormState,
    selectedTradesCount: number,
    selectedServiceAreasCount: number
): FormValidationResult {
    const fieldErrors: Record<string, string> = {
        ...validateBasicFields(form),
        ...validatePhysicalAddress(form),
        ...validateContactInfo(form),
        ...validateBusinessDetails(form),
        ...validateW9(form),
        ...validateEvidenceOfCoverage(form),
        ...validateFederalCertifications(form),
        ...validateTradesAndServiceAreas(
            form,
            selectedTradesCount,
            selectedServiceAreasCount
        ),
        ...validateHeldLicenses(form),
    };

    return {
        isValid: Object.keys(fieldErrors).length === 0,
        fieldErrors,
    };
}

// Quick check for a single field (for UI display)
export function isFieldValid(
    fieldName: string,
    form: FormState,
    selectedTradesCount?: number,
    selectedServiceAreasCount?: number
): boolean {
    const result = validateForm(
        form,
        selectedTradesCount ?? 0,
        selectedServiceAreasCount ?? 0
    );
    return !(fieldName in result.fieldErrors);
}

// Get error message for a specific field
export function getFieldError(
    fieldName: string,
    form: FormState,
    selectedTradesCount?: number,
    selectedServiceAreasCount?: number
): string | undefined {
    const result = validateForm(
        form,
        selectedTradesCount ?? 0,
        selectedServiceAreasCount ?? 0
    );
    return result.fieldErrors[fieldName];
}
